type AboutPageProps = {
  onNavigate?: (path: string) => void;
};

export function AboutPage({ onNavigate: _onNavigate }: AboutPageProps) {
  const companyProfile = [
    { label: "設立日", value: "2021年6月3日" },
    { label: "代表者", value: "原沢陽水" },
    { label: "拠点", value: "東京都千代田区二番町9-3 THE BASE 麹町1F" },
  ] as const;

  type HistoryCategory =
    | "設立"
    | "Hokusai"
    | "Centrum"
    | "オランダ子会社"
    | "ステーブルコイン関連事業"
    | "その他";

  const history: { date: string; category: HistoryCategory; title: string }[] = [
    { date: "2021年6月", category: "設立", title: "日本モノバンドル株式会社として設立" },
    { date: "2021年7月", category: "Hokusai", title: "NFTインフラ「Hokusai API」事前登録開始" },
    { date: "2021年8月", category: "Hokusai", title: "取締役2名就任、技術・法務体制を強化" },
    { date: "2021年9月", category: "設立", title: "第三者割当増資を実施" },
    { date: "2022年2月", category: "設立", title: "モノバンドル株式会社へ社名変更" },
    { date: "2022年2月", category: "Hokusai", title: "京都支店を新規設置" },
    { date: "2022年3月", category: "その他", title: "デジタル資産監査「SuperAudit」提供開始" },
    { date: "2022年4月", category: "Hokusai", title: "Hokusai マルチチェーン対応（Ethereum・Arbitrum他）" },
    { date: "2022年4月", category: "その他", title: "Polygon Studios とパートナーシップ" },
    { date: "2022年6月", category: "設立", title: "取締役会設置会社へ移行、コーポレートバリュー策定" },
    { date: "2022年8月", category: "設立", title: "執行役員制度を導入" },
    { date: "2022年11月", category: "オランダ子会社", title: "ヨーロッパ拠点の子会社を設立" },
    { date: "2022年12月", category: "Hokusai", title: "SBペイメントと「Hokusai Payment」提供開始" },
    { date: "2023年3月", category: "その他", title: "Aptos等と「Nethergate」共催" },
    { date: "2023年7月", category: "Centrum", title: "Centrum が AWS Activate プロバイダーに認定" },
    { date: "2023年8月", category: "Centrum", title: "Centrum グランドオープン" },
    { date: "2023年10月", category: "Hokusai", title: "DeNA「NFTリテラシー検定」に技術支援" },
    { date: "2023年12月", category: "Centrum", title: "野村HD・ビットバンク・SARAH がスポンサーに" },
    { date: "2024年4月", category: "Centrum", title: "Japan Fintech Week 2024 イベント会場パートナー" },
    { date: "2024年6月", category: "Centrum", title: "EDCON 2024 とパートナーシップ締結" },
    { date: "2024年8月", category: "設立", title: "新代表取締役就任" },
    { date: "2024年8月", category: "Centrum", title: "ETHTokyo・Solana Super Tokyo にパートナー連携" },
    { date: "2025年2月", category: "Centrum", title: "開発者コミュニティでノード運用を開始" },
    { date: "2025年7月", category: "Centrum", title: "Centrumを終了" },
    { date: "2025年8月", category: "Centrum", title: "ハッカーコレクティブへ。「家」「出版」「場」のプロデュースを開始" },
    { date: "2025年10月", category: "ステーブルコイン関連事業", title: "ステーブルコイン関連事業を立ち上げ" },
    { date: "2026年4月", category: "その他", title: "越境EC支援事業を開始" },
  ];

  return (
    <>
      {/* Page header */}
      <section className="border-b border-border/60 px-6 py-16 md:px-12">
        <div className="mx-auto max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            About Us
          </p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
            企業情報
          </h1>
          <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
            私たちは、世界の変革と成長の最前線に立ち、
            持続可能な価値創造をともに実現するパートナーです。
          </p>
        </div>
      </section>

      {/* Company intro + History */}
      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-serif text-2xl font-light tracking-tight text-foreground md:text-3xl">
            沿革
          </h2>
          <div className="relative mt-12">
            {/* Vertical line: same horizontal center as dots (12px = 0.75rem) */}
            <div
              className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-border via-border to-transparent"
              aria-hidden
            />
            <ul className="space-y-8">
              {[...history].reverse().map((item, i) => (
                <li
                  key={`${item.date}-${item.title}-${i}`}
                  className="relative flex gap-6 pl-8 sm:pl-10"
                >
                  {/* Dot: center at 12px (left-3) so line and dot align */}
                  <span
                    className="absolute left-3 top-[0.35rem] h-2 w-2 -translate-x-1/2 shrink-0 rounded-full border border-border bg-background shadow-sm"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1 pb-1">
                    <p className="font-mono text-xs tabular-nums text-muted-foreground">
                      {item.date}
                    </p>
                    <p className="mt-2 text-[15px] leading-snug text-foreground/90">
                      {item.title}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Company profile */}
      <section className="border-t border-border/60 bg-muted/20 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-serif text-2xl font-light tracking-tight text-foreground md:text-3xl">
            会社概要
          </h2>
          <div className="mt-10 overflow-hidden rounded-md border border-border/60">
            <table className="w-full min-w-[280px] border-collapse">
              <tbody>
                {companyProfile.map(({ label, value }) => (
                  <tr
                    key={label}
                    className="border-b border-border/60 last:border-b-0"
                  >
                    <th className="w-[120px] shrink-0 border-r border-border/60 bg-muted/30 px-4 py-3 text-left font-mono text-xs uppercase tracking-widest text-muted-foreground sm:w-[140px]">
                      {label}
                    </th>
                    <td className="px-4 py-3 text-foreground/90">
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
